import firebase from 'firebase/compat/app';
import { useEffect } from 'react';
import 'firebase/compat/messaging';
import { captureException, captureMessage } from '@sentry/react';
import { useRegisterPushMutation } from 'gcs-common/clients/api/entities/push-notifications/push-notifications-api';
import { checkPermissions } from 'gcs-common/components/NotificationPage/NotificationPage';
import { debugLogger } from 'gcs-common/helper/debugLogger'; // or your own logger
import changeSelectedChannelId from 'gcs-common/slices/channels/channelsThunks/changeSelectedChannelId';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { firebaseConfig, publicVapidKey, pushChannelName } from './firebase-constants';

/**
 * A custom Hook that sets up Firebase Messaging for web and handles notifications,
 */
export function useWebPushNotifications({ isNative = false }: { isNative?: boolean } = {}) {
  const dispatch = useDispatch();
  const registerPushMutation = useRegisterPushMutation();
  const navigate = useNavigate();

  function navigateToChat(channelId: string) {
    dispatch(changeSelectedChannelId({ selectedChannelId: channelId }));
  }

  useEffect(() => {
    /**
     * Initialize Firebase, request permission, and set up the service worker.
     */
    async function initFirebase() {
      try {
        if (isNative) {
          debugLogger('Native app detected, skipping web-specific firebase initialization.');
          return;
        }

        // If messaging is not supported, or if there's already an initialized app, bail out.
        if (!firebase.messaging.isSupported()) {
          debugLogger('Firebase messaging is not supported in this browser.');
          return;
        }

        if (firebase.apps.length) {
          debugLogger('Firebase app already initialized.');
          return;
        }

        firebase.initializeApp(firebaseConfig);
        const messaging = firebase.messaging();
        const swRegistration = await navigator.serviceWorker.register('/sw.js');

        // check permissions
        const permission = await checkPermissions();
        if (permission !== 'granted') {
          debugLogger('Notification permission not granted.');
          return;
        }

        const fcmToken = await messaging.getToken({
          vapidKey: publicVapidKey,
          serviceWorkerRegistration: swRegistration,
        });

        await registerPushMutation.mutateAsync({
          body: {
            token: fcmToken,
          },
        });

        /**
         * Listen for foreground messages via FCM.
         * We'll dispatch these messages to a BroadcastChannel so both
         * background and foreground messages follow the same flow.
         */
        const pushNotificationsChannel = new BroadcastChannel(pushChannelName);
        messaging.onMessage((payload: unknown) => {
          pushNotificationsChannel.postMessage({
            type: 'foregroundNotification',
            payload,
          });
        });

        /**
         * Catch broadcast messages from the service worker or from onMessage
         */
        pushNotificationsChannel.addEventListener(
          'message',
          (event: MessageEvent<{ type: string; payload: unknown }>) => {
            switch (event.data?.type) {
              case 'error':
                debugLogger('Error in ServiceWorker', event.data.payload);
                captureMessage('Error in ServiceWorker', {
                  level: 'error',
                  tags: { serviceworker: 'sw.js' },
                  extra: { error: event.data.payload },
                });
                break;

              case 'notification':
                // A new push notification arrived, start blinking
                break;

              case 'navigate_to_chat':
                // The service worker requests navigation to a specific chat
                if (typeof navigateToChat === 'function') {
                  navigateToChat(event.data?.payload as string);
                }
                break;

              case 'navigate_to_ticket':
                // The service worker requests navigation to a specific ticket
                if (typeof navigateToChat === 'function') {
                  navigate(event.data?.payload as string);
                }
                break;

              default:
                break;
            }
          },
        );
      } catch (error) {
        captureException(error);
        debugLogger('Could not register serviceWorker or initialize Firebase', error);
      }
    }

    void initFirebase();
  }, []);
}
